import { useMutation } from '@tanstack/react-query';

import { api } from 'api/v1/api';
import { addNotification } from 'notifications';
import { getApiErrorMessage, getApiSuccessMessage } from 'utils/getApiErrorMessage';

interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
  repeat_new_password: string;
}

interface SetPasswordRequest {
  new_password: string;
  repeat_new_password: string;
}

// Экраны настроек вызывают mutateAsync без catch («выстрелил и забыл»), и
// ответ 400 (например, «Неверный текущий пароль») превращался в необработанную
// ошибку страницы. Текст ошибки уже показывает onError, поэтому здесь
// отказ гасим: mutateAsync возвращает undefined вместо исключения.
export type PasswordField = 'old_password' | 'new_password' | 'repeat_new_password';

// Ответы API о пароле, которые относятся к конкретному полю формы. Такие ошибки
// экраны настроек показывают под полем (.fig-field-error), а не всплывашкой.
export const passwordApiFieldError = (
  error: unknown,
): { field: PasswordField; message: string } | null => {
  const message = getApiErrorMessage(error, '');
  if (!message) return null;
  const m = message.toLowerCase();
  if (m.includes('отличаться')) return { field: 'new_password', message };
  if (m.includes('текущий пароль')) return { field: 'old_password', message };
  if (m.includes('не совпадают')) return { field: 'repeat_new_password', message };
  if (m.includes('от 8 до 128')) return { field: 'new_password', message };
  return null;
};

const withSafeMutateAsync = <T extends { mutateAsync: (...args: never[]) => Promise<unknown> }>(
  mutation: T,
): T => ({
  ...mutation,
  mutateAsync: ((...args: Parameters<T['mutateAsync']>) =>
    mutation.mutateAsync(...args).catch(() => undefined)) as T['mutateAsync'],
});

export const useChangePassword = () => {
  return withSafeMutateAsync(useMutation({
    mutationFn: async (data: ChangePasswordRequest) => {
      const cleanData = {
        old_password: String(data.old_password || ''),
        new_password: String(data.new_password || ''),
        repeat_new_password: String(data.repeat_new_password || ''),
      };

      const response = await api.post('/password/change-request', cleanData, {
        skipGlobalErrorHandler: true,
      });
      return response.data;
    },
    onSuccess: (data) => {
      addNotification(
        getApiSuccessMessage(data, 'Проверьте почту для подтверждения смены пароля.'),
        'success',
      );
    },
    onError: (error) => {
      if (passwordApiFieldError(error)) return;
      addNotification(
        getApiErrorMessage(error, 'Не удалось отправить запрос на смену пароля'),
        'error',
      );
    },
  }));
};

export const useSetPassword = () => {
  return withSafeMutateAsync(useMutation({
    mutationFn: async (data: SetPasswordRequest) => {
      const response = await api.post(
        '/password/set',
        {
          new_password: String(data.new_password || ''),
          repeat_new_password: String(data.repeat_new_password || ''),
        },
        { skipGlobalErrorHandler: true },
      );
      return response.data;
    },
    onSuccess: (data) => {
      addNotification(getApiSuccessMessage(data, 'Пароль задан.'), 'success');
    },
    onError: (error) => {
      if (passwordApiFieldError(error)) return;
      addNotification(getApiErrorMessage(error, 'Не удалось задать пароль'), 'error');
    },
  }));
};

export const useConfirmPasswordChange = () => {
  return useMutation({
    mutationFn: async (data: { token: string }) => {
      const response = await api.post(
        '/password/confirm',
        { token: String(data.token || '') },
        { skipAuthRefresh: true, skipGlobalErrorHandler: true },
      );
      return response.data;
    },
    onSuccess: (data) => {
      addNotification(getApiSuccessMessage(data, 'Пароль успешно изменен'), 'success');
    },
    onError: (error) => {
      addNotification(
        getApiErrorMessage(error, 'Не удалось подтвердить смену пароля'),
        'error',
      );
    },
  });
};
